import DateInput from '../../components/DateInput';
/**
 * AdminWave5 — Iter 75 settings hub for Wave-5 features:
 *   • Slack/Teams webhook setup
 *   • Per-discipline SOAP template editor
 *   • Patient referral network outbound/inbound list
 *   • Group analytics dashboard
 *
 * Mounted at /admin/wave5 (admin & clinician).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Webhook, FileText, Network, BarChart3, Plus, Trash2, Send, Loader2, ChevronLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { safeConfirm } from '../../lib/confirm';

const DISCIPLINES = [
  ['physiotherapy', 'Physiotherapy'],
  ['osteopathy', 'Osteopathy'],
  ['chiropractic', 'Chiropractic'],
  ['massage_therapy', 'Massage therapy'],
];

const TABS = [
  { key: 'webhooks',   label: 'Slack / Teams',     icon: Webhook   },
  { key: 'templates',  label: 'SOAP templates',    icon: FileText  },
  { key: 'referrals',  label: 'Referral network',  icon: Network   },
  { key: 'analytics',  label: 'Group analytics',   icon: BarChart3 },
];

export default function AdminWave5() {
  const [tab, setTab] = useState('webhooks');
  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <Link to="/admin" className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-2" data-testid="w5-back">
          <ChevronLeft className="w-3 h-3"/> Back to dashboard
        </Link>
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CLINIC ESSENTIALS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="w5-title">
          Integrations, templates, referrals & analytics
        </h1>
      </div>

      <div className="flex gap-1 p-1 rounded-lg border border-soft bg-bg-secondary/40 mb-4 overflow-x-auto" data-testid="w5-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 px-3 rounded transition ${active ? 'bg-bg-primary shadow-sm font-semibold' : 'text-fg-muted'}`}
                    data-testid={`w5-tab-${t.key}`}>
              <Icon className="w-3.5 h-3.5"/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'webhooks'  && <WebhooksTab/>}
      {tab === 'templates' && <SoapTemplatesTab/>}
      {tab === 'referrals' && <ReferralsTab/>}
      {tab === 'analytics' && <AnalyticsTab/>}
    </div>
  );
}

// ───── Webhooks ─────
function WebhooksTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ label: '', target: 'slack', url: '' });
  const [busy, setBusy] = useState(false);
  const reload = () => api.get('/integrations/webhook/booking').then(r => setRows(r.data || []));
  useEffect(() => { reload(); }, []);
  const add = async () => {
    if (!form.label || !form.url) return toast.error('Label + URL required.');
    setBusy(true);
    try { await api.post('/integrations/webhook/booking', form); setForm({ label: '', target: 'slack', url: '' }); await reload(); toast.success('Added.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Add failed.'); }
    finally { setBusy(false); }
  };
  const test = async (id) => {
    try { const { data } = await api.post(`/integrations/webhook/booking/${id}/test`); toast.success(`HTTP ${data.status} from webhook.`); await reload(); }
    catch (e) { toast.error('Test failed.'); }
  };
  const remove = async (id) => {
    if (!await safeConfirm('Remove this webhook?')) return;
    await api.delete(`/integrations/webhook/booking/${id}`); await reload();
  };
  return (
    <div className="space-y-4">
      <div className="bento-card">
        <h3 className="font-heading font-semibold mb-3">Add a webhook</h3>
        <div className="grid md:grid-cols-4 gap-2">
          <input className="input md:col-span-1" placeholder='Label e.g. "#bookings"' value={form.label}
                 onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="wh-label"/>
          <select className="input" value={form.target}
                  onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="wh-target">
            <option value="slack">Slack</option>
            <option value="teams">Microsoft Teams</option>
            <option value="generic">Generic JSON</option>
          </select>
          <input className="input md:col-span-2" placeholder="Webhook URL" value={form.url}
                 onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="wh-url"/>
        </div>
        <button onClick={add} disabled={busy}
                className="btn-primary mt-3 inline-flex items-center gap-2"
                data-testid="wh-add">
          <Plus className="w-4 h-4"/> Add webhook
        </button>
      </div>
      <div className="bento-card">
        <h3 className="font-heading font-semibold mb-3">Active webhooks</h3>
        {rows.length === 0 ? <p className="text-sm text-fg-muted">No webhooks yet.</p> : (
          <div className="space-y-2" data-testid="wh-list">
            {rows.map((w) => (
              <div key={w.webhook_id} className="flex items-center gap-3 p-3 rounded-md border border-soft" data-testid={`wh-row-${w.webhook_id.slice(0,8)}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{w.label} <span className="text-fg-muted text-xs">({w.target})</span></div>
                  <div className="text-[11px] text-fg-muted truncate font-mono">{w.url}</div>
                  {w.last_status != null && <div className="text-[11px] text-fg-muted">Last fire: HTTP {w.last_status}</div>}
                </div>
                <button onClick={() => test(w.webhook_id)} className="btn-secondary p-1.5" title="Test" data-testid={`wh-test-${w.webhook_id.slice(0,8)}`}><Send className="w-3.5 h-3.5"/></button>
                <button onClick={() => remove(w.webhook_id)} className="btn-secondary p-1.5 text-[#B71C1C]" title="Remove" data-testid={`wh-del-${w.webhook_id.slice(0,8)}`}><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ───── SOAP templates ─────
const SOAP_PLACEHOLDERS = {
  subjective: 'CC: …\nHPI: onset/mechanism…\nPMH: …\nMeds: …',
  objective:  'Observation:\nAROM: \nPROM: \nStrength: \nSpecial tests: ',
  assessment: 'Working dx:\nClinical reasoning:\nPrognosis:',
  plan:       'Tx today:\nHEP:\nFollow-up:\nReferral:',
  data:       '',
  focus:      '',
  action:     '',
  response:   '',
};

function SoapTemplatesTab() {
  const [discipline, setDiscipline] = useState('physiotherapy');
  const [tpl, setTpl] = useState({});
  const [pristine, setPristine] = useState({});
  const [busy, setBusy] = useState(false);
  const isOverride = !!tpl._is_override;
  const dirty = JSON.stringify(tpl) !== JSON.stringify(pristine);
  const load = (d) =>
    api.get(`/templates/soap/${d}`).then((r) => {
      setTpl(r.data || {}); setPristine(r.data || {});
    });
  useEffect(() => { load(discipline); }, [discipline]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/templates/soap/${discipline}`, {
        ...tpl, discipline,
      });
      setTpl({ ...tpl, ...data, _is_override: true });
      setPristine({ ...tpl, ...data, _is_override: true });
      toast.success('Template saved.');
    } catch (e) { toast.error('Save failed.'); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!await safeConfirm('Discard your overrides and restore the built-in default template?')) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/templates/soap/${discipline}`);
      setTpl(data); setPristine(data);
      toast.success('Reset to default.');
    } catch (e) { toast.error('Reset failed.'); }
    finally { setBusy(false); }
  };
  const F = (k, label) => (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1 flex items-center justify-between">
        <span>{label}</span>
        {(tpl[k] || '').length > 0 && (
          <span className="text-[10px] text-fg-muted/70">{(tpl[k] || '').length} chars</span>
        )}
      </div>
      <textarea className="input min-h-[110px] font-mono text-xs"
                value={tpl[k] || ''}
                placeholder={SOAP_PLACEHOLDERS[k] || ''}
                onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })}
                data-testid={`tpl-${k}`}/>
    </label>
  );
  return (
    <div className="bento-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <select className="input max-w-[280px]" value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)} data-testid="tpl-discipline">
            {DISCIPLINES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <span data-testid="tpl-status"
                className={`text-[11px] px-2 py-0.5 rounded-full border ${isOverride ? 'bg-[#FFF6E2] text-[#7A5500] border-[#FFB000]/40' : 'bg-bg-secondary/40 text-fg-muted border-soft'}`}>
            {isOverride ? 'Custom override' : 'Using built-in default'}
          </span>
          {dirty && (
            <span className="text-[11px] text-[#B83A3A]" data-testid="tpl-dirty">• Unsaved changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOverride && (
            <button onClick={reset} disabled={busy}
                    className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50 text-xs"
                    data-testid="tpl-reset">
              <Trash2 className="w-3.5 h-3.5"/> Reset to default
            </button>
          )}
          <button onClick={save} disabled={busy || !dirty}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="tpl-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save template
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {F('subjective', 'Subjective')}
        {F('objective', 'Objective')}
        {F('assessment', 'Assessment')}
        {F('plan', 'Plan')}
      </div>
      <p className="text-[11px] text-fg-muted mt-3">
        Templates pre-fill the clinical-note editor when a clinician picks this
        discipline. Clinicians can still edit each section freely after pre-fill.
        {tpl._updated_at && (
          <> Last updated <strong>{new Date(tpl._updated_at).toLocaleString()}</strong>.</>
        )}
      </p>
    </div>
  );
}

// ───── Referrals ─────
function ReferralsTab() {
  const [rows, setRows] = useState([]);
  const reload = () => api.get('/referrals/network').then(r => setRows(r.data || []));
  useEffect(() => { reload(); }, []);

  return (
    <div className="bento-card">
      <h3 className="font-heading font-semibold mb-3">Outbound + inbound referrals</h3>
      {rows.length === 0 ? <p className="text-sm text-fg-muted" data-testid="ref-empty">No referrals yet. Start one from a patient chart.</p> : (
        <div className="space-y-2" data-testid="ref-list">
          {rows.map((r) => (
            <div key={r.referral_id} className="flex items-center gap-3 p-3 rounded-md border border-soft" data-testid={`ref-row-${r.referral_id.slice(0,8)}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.patient_name_snapshot}</div>
                <div className="text-[11px] text-fg-muted">
                  {r.sending_clinic_id} → {r.receiving_clinic_id} · {r.discipline || '—'} · {r.revenue_share_pct}% rev share
                </div>
                {r.reason && <div className="text-[11px] text-fg-muted italic">"{r.reason}"</div>}
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                r.status === 'completed' ? 'bg-[#1B5E20]/10 text-[#1B5E20]' :
                r.status === 'accepted' ? 'bg-[#FFB000]/15 text-[#A86F31]' :
                'bg-fg-muted/10 text-fg-muted'
              }`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-fg-muted mt-3">
        To create a new outbound referral, open a patient chart and use the
        "Refer to partner clinic" action (coming soon — backend ready,
        UI lives in v1.1 patient chart).
      </p>
    </div>
  );
}

// ───── Group analytics ─────
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(() => new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const reload = async () => {
    const { data } = await api.get(`/analytics/group?from_=${from}T00:00:00Z&to=${to}T23:59:59Z`);
    setData(data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  if (!data) return <div className="bento-card text-sm text-fg-muted">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="bento-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-heading font-semibold">Group analytics</h3>
          <div className="flex items-center gap-2">
            <DateInput className="text-xs" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ga-from"/>
            <span className="text-xs text-fg-muted">→</span>
            <DateInput className="text-xs" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ga-to"/>
            <button onClick={reload} className="btn-secondary text-xs" data-testid="ga-go">Refresh</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Revenue" value={`€${data.totals.revenue.toFixed(2)}`} testid="ga-revenue"/>
          <Stat label="Outstanding" value={`€${data.totals.outstanding.toFixed(2)}`} testid="ga-outstanding"/>
          <Stat label="Appointments" value={data.totals.appointments} testid="ga-appts"/>
          <Stat label="Retention" value={`${data.totals.retention_pct}%`} testid="ga-retention"/>
        </div>
      </div>
      <div className="bento-card">
        <h3 className="font-heading font-semibold mb-3">By location</h3>
        {data.locations.length === 0 ? <p className="text-sm text-fg-muted">No locations.</p> : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-fg-muted">
              <tr><th className="text-left p-2">Location</th><th className="text-right p-2">Revenue</th><th className="text-right p-2">Outstanding</th><th className="text-right p-2">Appts</th><th className="text-right p-2">Patients</th></tr>
            </thead>
            <tbody data-testid="ga-locations">
              {data.locations.map((l, i) => (
                <tr key={i} className="border-t border-soft">
                  <td className="p-2">{l.name}</td>
                  <td className="p-2 text-right">€{l.revenue.toFixed(2)}</td>
                  <td className="p-2 text-right">€{l.outstanding.toFixed(2)}</td>
                  <td className="p-2 text-right">{l.appointments}</td>
                  <td className="p-2 text-right">{l.patients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md border border-soft p-3 bg-bg-secondary/30" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="text-2xl font-heading font-bold mt-1">{value}</div>
    </div>
  );
}
